/**
 * opportunityStages — what a UI stage move may WRITE.
 *
 * ⛔⛔ THERE ARE TWO VOCABULARIES IN crm_opportunities.stage, AND THIS FILE
 * DESCRIBES ONLY ONE OF THEM — Card P95c item 1.
 *
 * ⚠⚠ MY P95b COMMENT SAID THIS LIST WAS "the ONE vocabulary
 * crm_opportunities.stage may hold". THAT WAS FALSE, and the error is the
 * dangerous direction: it invites a writer to normalise anything else away.
 *
 *   · HAND-MADE rows carry these seven SLUGS (lead … closed_lost). The
 *     Opportunities board, its filter, the edit form and the drag all group by
 *     them, and the column's schema default is 'lead'.
 *   · GHL-IMPORTED rows carry Carlos's CANONICAL 14 NAMES from
 *     crm_pipeline_stages (migrations 136/137) — e.g. 'Doctor Consult Booked'
 *     — written by database/ghlOpportunityUpsert.js. The forecast
 *     (routes/_pipeline_forecast_calc.js) and the workflow role definitions
 *     (routes/crm.js) KEY ON THOSE LONG NAMES; the forecast's probability map
 *     carries both sets and calls the slugs "legacy pre-taxonomy short codes".
 *
 * ⛔ SO THIS LIST IS NOT "WHAT THE COLUMN MAY HOLD". It is "what a UI stage
 * move may WRITE". Rewriting an imported row's canonical name to a slug does
 * not tidy anything: it drops that opportunity out of the forecast tier it
 * belonged to and out of the PEM's owned_stages, silently.
 *
 * ⚠ RECONCILING THE TWO IS A DECISION FOR DANIEL AND CARLOS, and is
 * deliberately NOT attempted here. This card only stops the contact drawer and
 * the other UI writers from damaging imported rows in the meantime.
 */
export const OPPORTUNITY_STAGES = Object.freeze([
  'lead', 'contacted', 'qualified', 'proposal', 'negotiation', 'closed_won', 'closed_lost',
]);

export const OPPORTUNITY_STAGE_LABELS = Object.freeze({
  lead: 'Lead',
  contacted: 'Contacted',
  qualified: 'Qualified',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
});

/** Is this a value a UI stage move may WRITE? (Not: may the column hold it.) */
export function isOpportunityStage(value) {
  return typeof value === 'string' && OPPORTUNITY_STAGES.includes(value);
}

/**
 * Is this row's stage one the UI owns, or a canonical imported name?
 *
 * ⛔ A row whose stage is NOT a slug is managed by the GHL pipeline. The drawer
 * may show it, and must not offer to rewrite it.
 */
export function isUiManagedStage(currentStage) {
  return isOpportunityStage(currentStage);
}


/**
 * Is this opportunity owned by the GHL pipeline?
 *
 * ⛔⛔ A ROW FROM GHL IS READ-ONLY IN THIS UI DURING THE COMPARISON PERIOD —
 * Card P95f item 1, and the reason is a one-way door.
 *
 * ANY save of an imported row stamps crm_opportunities.zgx_touched_at
 * (migration 149's ratchet trigger) and sets last_modified_source =
 * 'zignagenix'. database/ghlOpportunityUpsert.js then sees a non-null
 * zgx_touched_at and records "skipped_zgx_owned" — "never overwrite a
 * rep-touched record" — FOR EVER. So one note edit silently cuts that deal
 * off from GHL permanently.
 *
 * ⚠⚠ AND P95c CLOSED THE OTHER ROUTE. It refuses a UI stage move on these
 * rows. So after one accidental save the stage can never change again by
 * EITHER path — while our own message tells the rep to go and change it in
 * GHL, where the change will now never arrive.
 *
 * ⚠ BOTH DISCRIMINATORS, not just the stage. A ghl_opportunity_id means the
 * row came from GHL whatever its stage happens to read; a canonical stage
 * means it came from GHL even if the id column is not populated on this
 * deployment. Either is sufficient, and using only one leaves a gap.
 */
export function isGhlManagedRow(row) {
  if (!row) return false;
  if (row.ghl_opportunity_id) return true;
  return !isUiManagedStage(row.stage);
}

/** What a rep is told about a deal this UI will not edit. */
export const GHL_MANAGED_SENTENCE =
  'This deal comes from GHL. Make changes in GHL; they appear here within about 15 minutes.';

export default OPPORTUNITY_STAGES;
